<template>
  <section class="financial-conciliation-tab">
    <AppSectionTabs
      v-model="activeInnerTab"
      :tabs="innerTabs"
      :aria-label="text.tabsAriaLabel"
    />

    <FinancialConciliationToolbar
      :key="activeInnerTab"
      :labels="text.toolbar"
      :filter-options="toolbarOptions"
      :active-tab="activeInnerTab"
      @search-change="handleToolbarSearchChange"
      @filters-change="handleToolbarFiltersChange"
      @apply="handleToolbarApply"
    />

    <div class="app-table-wrap">
      <div class="app-table-scroll">
        <table class="app-data-table">
          <thead>
            <tr v-if="isUnassignedTab">
              <th>{{ text.table.unassigned.date }}</th>
              <th>{{ text.table.unassigned.type }}</th>
              <th>{{ text.table.unassigned.concept }}</th>
              <th class="app-text-right">{{ text.table.unassigned.amount }}</th>
              <th></th>
            </tr>
            <tr v-else>
              <th>{{ text.table.assigned.date }}</th>
              <th>{{ text.table.assigned.account }}</th>
              <th>{{ text.table.assigned.movement }}</th>
              <th>{{ text.table.assigned.concept }}</th>
              <th class="app-text-right">{{ text.table.assigned.deposit }}</th>
              <th class="app-text-right">{{ text.table.assigned.withdrawal }}</th>
              <th class="app-text-right">{{ text.table.assigned.balance }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="isUnassignedTab ? 5 : 8" class="app-table-empty">{{ text.loading }}</td>
            </tr>

            <tr v-else-if="filteredRows.length === 0">
              <td :colspan="isUnassignedTab ? 5 : 8" class="app-table-empty">{{ text.empty }}</td>
            </tr>

            <template v-else>
              <tr v-if="isUnassignedTab" v-for="row in filteredRows" :key="`unassigned-${row.id}`">
                <td>
                  <div class="app-strong">{{ row.date }}</div>
                </td>
                <td>
                  <span
                    class="financial-category-pill"
                    :class="row.type_key === 'income' ? 'financial-category-pill--income' : 'financial-category-pill--expense'"
                  >
                    {{ row.type }}
                  </span>
                </td>
                <td>
                  <div class="app-strong">
                    {{ row.concept_title || row.concept }}
                    <template v-if="row.concept_subtitle">
                      <router-link
                        v-if="resolveConceptSubtitleRoute(row)"
                        :to="resolveConceptSubtitleRoute(row)"
                      >
                        {{ row.concept_subtitle }}
                      </router-link>
                      <span v-else>{{ row.concept_subtitle }}</span>
                    </template>
                  </div>

                  <div class="financial-conciliation-concept-detail-row">
                    <small class="app-muted">
                      {{ row.concept_detail_label || 'Concepto:' }}
                      <router-link
                        v-if="resolveConceptDetailRoute(row)"
                        :to="resolveConceptDetailRoute(row)"
                      >
                        {{ row.concept_detail || row.concept || '' }}
                      </router-link>
                      <span v-else>{{ row.concept_detail || row.concept || '' }}</span>
                    </small>
                    <small class="app-muted">{{ row.concept_detail_amount_label || '' }}</small>
                  </div>
                </td>
                <td class="app-text-right">
                  <strong>{{ row.amount_label }}</strong>
                </td>
                <td class="app-table-actions-cell">
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--edit"
                    :title="text.table.actions.review"
                    @click="reviewRow(row)"
                  >
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>

              <tr v-else v-for="row in filteredRows" :key="`assigned-${row.id}`">
                <td>
                  <div class="app-strong">{{ row.date }}</div>
                  <small class="app-muted">{{ row.time ? `${row.time} hrs` : '' }}</small>
                </td>
                <td>
                  <div class="app-strong">{{ row.financial_account_name || '-' }}</div>
                </td>
                <td>
                  <span class="financial-category-pill" :class="resolveCategoryClass(resolveMovementKey(row))">
                    {{ resolveMovementLabel(row) }}
                  </span>
                </td>
                <td>
                  <div class="app-strong">
                    {{ row.concept_title || row.concept }}
                    <template v-if="row.concept_subtitle">
                      <router-link
                        v-if="resolveConceptSubtitleRoute(row)"
                        :to="resolveConceptSubtitleRoute(row)"
                      >
                        {{ row.concept_subtitle }}
                      </router-link>
                      <span v-else>{{ row.concept_subtitle }}</span>
                    </template>
                  </div>

                  <div class="financial-conciliation-concept-detail-row">
                    <small class="app-muted">
                      {{ row.concept_detail_label || 'Concepto:' }}
                      <router-link
                        v-if="resolveConceptDetailRoute(row)"
                        :to="resolveConceptDetailRoute(row)"
                      >
                        {{ row.concept_detail || row.concept || '' }}
                      </router-link>
                      <span v-else>{{ row.concept_detail || row.concept || '' }}</span>
                    </small>
                    <small class="app-muted">{{ row.concept_detail_amount_label || '' }}</small>
                  </div>
                </td>
                <td class="app-text-right">
                  <strong class="financial-amount-positive">{{ row.deposit_label }}</strong>
                </td>
                <td class="app-text-right">
                  <strong class="financial-amount-negative">{{ row.withdrawal_label }}</strong>
                </td>
                <td class="app-text-right">
                  <strong>{{ row.balance_label }}</strong>
                </td>
                <td class="app-table-actions-cell">
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--edit"
                    :title="text.table.actions.review"
                    @click="reviewRow(row)"
                  >
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <FinancialConciliationReviewModal
      :show="isReviewModalOpen"
      :loading="reviewSaving || reviewDeleting"
      :text="text"
      :accounts="toolbarOptions.account"
      :row="reviewingRow"
      @close="closeReviewModal"
      @submit="submitReview"
      @request-delete="openDeleteReviewConfirm"
    />

    <ConfirmActionModal
      :show="isDeleteReviewConfirmOpen"
      tone="danger"
      :loading="reviewDeleting"
      :title="text.modal.confirmDelete.title"
      :subtitle="text.modal.confirmDelete.subtitle"
      :message="text.modal.confirmDelete.message"
      :confirm-text="text.modal.confirmDelete.confirm"
      :cancel-text="text.modal.actions.cancel"
      @confirm="confirmDeleteReview"
      @cancel="closeDeleteReviewConfirm"
    />
  </section>
</template>

<script>
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import FinancialConciliationToolbar from '@/components/financial/FinancialConciliationToolbar.vue';
import FinancialConciliationReviewModal from '@/components/financial/modals/FinancialConciliationReviewModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import financialReconciliationService from '@/services/financialReconciliation';
import { extractPayload } from '@/utils/dataNormalization';
import { buildComparableHaystack, toComparableSearchText } from '@/utils/textValue';
import { buildCurrentMonthApiRange } from '@/utils/datePresentation';

export default {
  name: 'FinancialConciliationTab',
  components: {
    AppSectionTabs,
    FinancialConciliationToolbar,
    FinancialConciliationReviewModal,
    ConfirmActionModal,
  },
  props: {
    text: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeInnerTab: 'unassigned',
      loading: false,
      rows: [],
      search: '',
      activeFilters: {
        ...buildCurrentMonthApiRange(),
        customer_ids: [],
        financial_account_ids: [],
        supplier_ids: [],
        collaborator_ids: [],
        movement_type: 'all',
        type: 'all',
      },
      lastFetchedFiltersSignature: '',
      toolbarOptions: {
        customer: [],
        account: [],
        supplier: [],
        collaborator: [],
      },
      isReviewModalOpen: false,
      reviewingRow: null,
      reviewSaving: false,
      reviewDeleting: false,
      isDeleteReviewConfirmOpen: false,
    };
  },
  computed: {
    innerTabs() {
      return [
        { key: 'unassigned', label: this.text.tabs.unassigned },
        { key: 'assigned', label: this.text.tabs.assigned },
      ];
    },
    isUnassignedTab() {
      return this.activeInnerTab === 'unassigned';
    },
    filteredRows() {
      const sourceRows = Array.isArray(this.rows) ? this.rows : [];
      const searchTerm = toComparableSearchText(this.search);

      if (searchTerm === '') {
        return sourceRows;
      }

      return sourceRows.filter((row) => {
        const movementLabel = this.isUnassignedTab
          ? String(row?.type || '').trim()
          : this.resolveMovementLabel(row);

        const haystack = buildComparableHaystack([
          row?.date,
          row?.time,
          row?.type,
          row?.type_key,
          row?.movement,
          row?.movement_key,
          movementLabel,
          row?.financial_account_name,
          row?.concept,
          row?.concept_title,
          row?.concept_subtitle,
          row?.concept_detail_label,
          row?.concept_detail,
          row?.concept_detail_amount_label,
          row?.reference,
          row?.reference_id,
          row?.amount,
          row?.amount_label,
          row?.deposit_label,
          row?.withdrawal_label,
          row?.balance,
          row?.balance_label,
          row?.currency,
        ]);

        return haystack.includes(searchTerm);
      });
    },
  },
  watch: {
    activeInnerTab() {
      this.fetchRows();
    },
  },
  created() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      await Promise.all([
        this.loadSelectors(),
        this.fetchRows(),
      ]);
    },
    async loadSelectors() {
      try {
        const response = await financialReconciliationService.selectors({}, { skipLoading: true });
        const payload = extractPayload(response);

        this.toolbarOptions = {
          customer: Array.isArray(payload?.customers) ? payload.customers : [],
          account: Array.isArray(payload?.accounts) ? payload.accounts : [],
          supplier: Array.isArray(payload?.suppliers) ? payload.suppliers : [],
          collaborator: Array.isArray(payload?.collaborators) ? payload.collaborators : [],
        };
      } catch (error) {
        this.toolbarOptions = {
          customer: [],
          account: [],
          supplier: [],
          collaborator: [],
        };
      }
    },
    async fetchRows() {
      this.loading = true;

      try {
        const response = await financialReconciliationService.list({
          tab: this.activeInnerTab,
          ...this.activeFilters,
        }, { skipLoading: true });

        const payload = extractPayload(response);
        this.rows = Array.isArray(payload?.rows) ? payload.rows : [];
        this.lastFetchedFiltersSignature = this.buildFiltersSignature(this.activeFilters);
      } catch (error) {
        this.rows = [];
        const message = error?.response?.data?.message || this.text.messages.loadError;
        this.$toast?.error(message);
      } finally {
        this.loading = false;
      }
    },
    handleToolbarSearchChange(value) {
      this.search = String(value || '');
    },
    handleToolbarFiltersChange(filters = {}) {
      this.activeFilters = {
        date_from: String(filters?.date_from || '').trim(),
        date_to: String(filters?.date_to || '').trim(),
        customer_ids: Array.isArray(filters?.customer_ids) ? filters.customer_ids : [],
        financial_account_ids: Array.isArray(filters?.financial_account_ids) ? filters.financial_account_ids : [],
        supplier_ids: Array.isArray(filters?.supplier_ids) ? filters.supplier_ids : [],
        collaborator_ids: Array.isArray(filters?.collaborator_ids) ? filters.collaborator_ids : [],
        movement_type: String(filters?.movement_type || 'all').trim() || 'all',
        type: String(filters?.type || 'all').trim() || 'all',
      };
    },
    async handleToolbarApply(payload) {
      this.handleToolbarSearchChange(payload?.search);
      this.handleToolbarFiltersChange(payload?.filters || {});
      const nextFiltersSignature = this.buildFiltersSignature(this.activeFilters);

      if (nextFiltersSignature !== this.lastFetchedFiltersSignature) {
        await this.fetchRows();
      }
    },
    buildFiltersSignature(filters = {}) {
      return JSON.stringify({
        tab: String(this.activeInnerTab || '').trim(),
        date_from: String(filters?.date_from || '').trim(),
        date_to: String(filters?.date_to || '').trim(),
        customer_ids: Array.isArray(filters?.customer_ids) ? filters.customer_ids : [],
        financial_account_ids: Array.isArray(filters?.financial_account_ids) ? filters.financial_account_ids : [],
        supplier_ids: Array.isArray(filters?.supplier_ids) ? filters.supplier_ids : [],
        collaborator_ids: Array.isArray(filters?.collaborator_ids) ? filters.collaborator_ids : [],
        movement_type: String(filters?.movement_type || 'all').trim() || 'all',
        type: String(filters?.type || 'all').trim() || 'all',
      });
    },
    resolveCategoryClass(categoryKey) {
      const normalized = String(categoryKey || '').trim().toLowerCase();
      if (normalized === 'income') return 'financial-category-pill--income';
      if (normalized === 'expense') return 'financial-category-pill--expense';
      if (normalized === 'transfer') return 'financial-category-pill--transfer';
      if (normalized === 'replacement') return 'financial-category-pill--replacement';
      if (normalized === 'init') return 'financial-category-pill--init';
      if (normalized === 'otherincome') return 'financial-category-pill--otherincome';
      if (normalized === 'otherexpense') return 'financial-category-pill--otherexpense';
      return 'financial-category-pill--neutral';
    },
    resolveMovementKey(row) {
      const normalized = String(row?.movement_key || '').trim().toLowerCase();
      if (normalized !== '' && normalized !== 'unknown') {
        return normalized;
      }

      return Number(row?.is_income || 0) === 1 ? 'income' : 'expense';
    },
    resolveMovementLabel(row) {
      const label = String(row?.movement || '').trim();
      if (label !== '') {
        return label;
      }

      return Number(row?.is_income || 0) === 1 ? 'Ingreso' : 'Egreso';
    },
    resolveConceptSubtitleRoute(row) {
      const subtitleType = String(row?.concept_subtitle_type || '').trim().toLowerCase();
      const subtitleId = Number(row?.concept_subtitle_id || 0);

      if (subtitleId <= 0) {
        return null;
      }

      if (subtitleType === 'customer') {
        return {
          name: 'customerDetail',
          params: { customerId: String(subtitleId) },
        };
      }

      if (subtitleType === 'supplier') {
        return {
          name: 'supplierDetail',
          params: { supplierId: String(subtitleId) },
        };
      }

      return null;
    },
    resolveConceptDetailRoute(row) {
      const label = String(row?.concept_detail_label || '').trim().toLowerCase();
      const invoiceId = Number(row?.concept_detail_invoice_id || 0);
      if (label === 'factura:' && invoiceId > 0) {
        return {
          name: 'invoiceDetail',
          params: { invoiceId: String(invoiceId) },
        };
      }

      const referenceType = String(row?.reference_type || '').trim().toLowerCase();
      const typeReference = String(row?.type_reference || '').trim().toLowerCase();
      const rawReferenceId = Number(row?.reference_id || row?.reference || 0);
      const referenceId = Number.isFinite(rawReferenceId) ? rawReferenceId : 0;

      if (referenceId <= 0) {
        return null;
      }

      if (referenceType === 'payment_receipt' || typeReference === 'income') {
        return {
          name: 'paymentReceiptDetail',
          params: { paymentReceiptId: String(referenceId) },
        };
      }

      if (referenceType === 'expense_receipt' || typeReference === 'expense') {
        return {
          name: 'expenseReceiptDetail',
          params: { expenseReceiptId: String(referenceId) },
        };
      }

      return null;
    },
    reviewRow(row) {
      const normalized = row && typeof row === 'object' ? { ...row } : null;
      if (!normalized) {
        return;
      }

      this.reviewingRow = normalized;
      this.isReviewModalOpen = true;
    },
    closeReviewModal(force = false) {
      if (!force && (this.reviewSaving || this.reviewDeleting)) {
        return;
      }

      this.isReviewModalOpen = false;
      this.reviewingRow = null;
    },
    async submitReview(payload) {
      if (this.reviewSaving || this.reviewDeleting) {
        return;
      }

      this.reviewSaving = true;

      try {
        const response = await financialReconciliationService.saveReview(payload || {});
        this.$toast?.success(response?.message || this.text.messages.saveSuccess);
        this.closeReviewModal(true);
        await this.fetchRows();
      } catch (error) {
        const message = error?.response?.data?.message || this.text.messages.saveError;
        this.$toast?.error(message);
      } finally {
        this.reviewSaving = false;
      }
    },
    openDeleteReviewConfirm() {
      if (Number(this.reviewingRow?.can_delete || 0) !== 1) {
        return;
      }

      this.isDeleteReviewConfirmOpen = true;
    },
    closeDeleteReviewConfirm(force = false) {
      if (!force && this.reviewDeleting) {
        return;
      }

      this.isDeleteReviewConfirmOpen = false;
    },
    async confirmDeleteReview() {
      const transactionId = Number(this.reviewingRow?.id || 0);
      if (transactionId <= 0 || this.reviewDeleting || Number(this.reviewingRow?.can_delete || 0) !== 1) {
        this.closeDeleteReviewConfirm();
        return;
      }

      this.reviewDeleting = true;

      try {
        const response = await financialReconciliationService.deleteReview(transactionId);
        this.$toast?.success(response?.message || this.text.messages.deleteSuccess);
        this.closeDeleteReviewConfirm(true);
        this.closeReviewModal(true);
        await this.fetchRows();
      } catch (error) {
        const message = error?.response?.data?.message || this.text.messages.deleteError;
        this.$toast?.error(message);
      } finally {
        this.reviewDeleting = false;
      }
    },
  },
};
</script>

<style scoped>
.financial-conciliation-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-category-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: 999px;
  padding: 0 10px;
  font-size: 11px;
  border: 1px solid transparent;
}

.financial-category-pill--income {
  color: #14532d;
  background: #dcfce7;
  border-color: #86efac;
}

.financial-category-pill--expense {
  color: #9f1239;
  background: #ffe4e6;
  border-color: #fda4af;
}

.financial-category-pill--transfer {
  color: #1e3a8a;
  background: #dbeafe;
  border-color: #93c5fd;
}

.financial-category-pill--replacement {
  color: #92400e;
  background: #fef3c7;
  border-color: #fcd34d;
}

.financial-category-pill--init {
  color: #6b21a8;
  background: #f3e8ff;
  border-color: #d8b4fe;
}

.financial-category-pill--otherincome {
  color: #065f46;
  background: #ccfbf1;
  border-color: #99f6e4;
}

.financial-category-pill--otherexpense {
  color: #7c2d12;
  background: #ffedd5;
  border-color: #fdba74;
}

.financial-category-pill--neutral {
  color: #334155;
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.financial-amount-positive {
  color: #16a34a;
}

.financial-amount-negative {
  color: #dc2626;
}

.financial-conciliation-concept-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.financial-conciliation-concept-detail-row .app-muted:first-child {
  flex: 1;
  min-width: 0;
}

.financial-conciliation-concept-detail-row .app-muted:last-child {
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
  min-width: 120px;
}
</style>
